import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import CheckoutModal from '../payment/CheckoutModal';
import ProfileCompletionModal from '../../features/auth/ProfileCompletionModal';

const LevelDetailModal = ({ level, allLevels = [], onClose }) => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [selectedDuration, setSelectedDuration] = useState(1);
    const hasCourses = level.pricing?.some(p => p.priceCourse > 0 && p.priceExamCourse > 0);
    // Default sur "Exam seul" pour TOUS les niveaux — décision UX produit. La formule examen
    // est la plus achetée ; l'user bascule manuellement vers "Cours" ou "Cours + Examens" si besoin.
    const [selectedFormula, setSelectedFormula] = useState('examOnly');
    const [showCheckout, setShowCheckout] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);

    const name = language === 'fr' ? level.libelleFr : level.libelleEn;
    const description = language === 'fr' ? level.newPageFr : level.newPageEn;

    const hasPricing = level.pricing?.length > 0;
    const hasDualPricing = level.pricing?.some(p => p.priceCourse != null && p.priceExamCourse != null);

    const durations = [1, 3, 9];

    const getDurationLabel = (months) => {
        if (months === 1) return language === 'fr' ? '1 mois' : '1 month';
        if (months === 3) return language === 'fr' ? '3 mois' : '3 months';
        if (months === 9) return language === 'fr' ? '9 mois' : '9 months';
        return `${months} ${language === 'fr' ? 'mois' : 'months'}`;
    };

    const getPrice = (duration, formula) => {
        const item = level.pricing?.find(p => p.duration === duration);
        if (!item) return null;
        if (item.priceCourse != null && item.priceExamCourse != null) {
            if (formula === 'noExam') return parseFloat(item.priceCourse);
            if (formula === 'examOnly') return parseFloat(item.priceExam || item.priceExamCourse * 0.5);
            return parseFloat(item.priceExamCourse);
        }
        if (item.priceExam != null) return parseFloat(item.priceExam);
        return null;
    };

    const currentPrice = getPrice(selectedDuration, selectedFormula);

    const handleBuy = () => {
        if (!user) {
            navigate(`/login?returnTo=${encodeURIComponent('/levels?level=' + level.idNiveau)}`);
            return;
        }
        if (!user.profileCompleted) {
            setShowProfileModal(true);
            return;
        }
        setShowCheckout(true);
    };

    // Included levels for packs
    const includedLevels = useMemo(() => {
        if (!level.isPack || !level.includedLevels) return [];
        return level.includedLevels
            .map(id => allLevels.find(l => l.idNiveau === id))
            .filter(Boolean);
    }, [level, allLevels]);

    return (
        <motion.div
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
        >
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal — slides from bottom to center */}
            <motion.div
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '100%', opacity: 0 }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="relative bg-white dark:bg-[#1a1a1a] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-lg max-h-[85vh] flex flex-col border border-gray-200 dark:border-white/10 overflow-hidden">
                {/* Color header bar */}
                <div
                    className="h-1.5"
                    style={{ backgroundColor: level.newBackgroundColor }}
                />

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
                    <div className="flex items-center gap-3">
                        <div
                            className="px-3 py-1.5 rounded-xl text-white font-extrabold text-sm shadow whitespace-nowrap"
                            style={{ backgroundColor: level.newBackgroundColor }}
                        >
                            {(name || level.var).toUpperCase()}
                        </div>
                        {level.isFree && (
                            <span className="text-xs font-bold text-green-600 dark:text-green-400">
                                {language === 'fr' ? 'Gratuit' : 'Free'}
                            </span>
                        )}
                        {level.isPack && (
                            <span className="text-xs font-bold text-purple-600 dark:text-purple-400">Pack</span>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body (scrollable) */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 custom-scrollbar">
                    {/* Description */}
                    {description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
                            {description}
                        </p>
                    )}

                    {/* Minimum score */}
                    {level.minimumPercentage != null && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-white/50">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>
                                <span className="font-semibold">{language === 'fr' ? 'Note minimale:' : 'Minimum score:'}</span> {level.minimumPercentage}%
                            </span>
                        </div>
                    )}

                    {/* Pack included levels */}
                    {includedLevels.length > 0 && (
                        <div>
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                {language === 'fr' ? 'Niveaux inclus' : 'Included levels'}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {includedLevels.map(l => {
                                    const lName = language === 'fr' ? l.libelleFr : l.libelleEn;
                                    return (
                                    <span
                                        key={l.idNiveau}
                                        className="px-3 py-1 rounded-full text-white text-xs font-bold whitespace-nowrap"
                                        style={{ backgroundColor: l.newBackgroundColor }}
                                    >
                                        {(lName || l.var).toUpperCase()}
                                    </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Free level badge */}
                    {level.isFree && (
                        <div className="bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-xl p-4 text-center">
                            <p className="text-green-700 dark:text-green-400 font-semibold">
                                {language === 'fr' ? 'Accès gratuit — Aucun abonnement requis' : 'Free access — No subscription required'}
                            </p>
                        </div>
                    )}

                    {/* Pricing section */}
                    {!level.isFree && hasPricing && (
                        <div className="space-y-5">
                            <div className="border-t border-gray-200 dark:border-white/10 pt-5">
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4">
                                    {language === 'fr' ? 'Choisir un abonnement' : 'Choose a subscription'}
                                </h3>

                                {/* Formula selector (dual pricing only) */}
                                {hasDualPricing && (
                                    <div className="mb-4">
                                        <p className="text-xs font-semibold text-gray-500 dark:text-white/50 mb-2 uppercase tracking-wide">
                                            {language === 'fr' ? 'Formule' : 'Formula'}
                                        </p>
                                        <div className="flex bg-gray-100 dark:bg-white/5 rounded-xl p-1 gap-1">
                                            {[
                                                { id: 'noExam', label: language === 'fr' ? 'Cours' : 'Courses' },
                                                { id: 'examOnly', label: language === 'fr' ? 'Examens' : 'Exams' },
                                                { id: 'withExam', label: language === 'fr' ? 'Cours + Examens' : 'Courses + Exams' },
                                            ].map(f => (
                                                <button
                                                    key={f.id}
                                                    onClick={() => setSelectedFormula(f.id)}
                                                    className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                                                        selectedFormula === f.id
                                                            ? 'bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
                                                            : 'text-gray-500 dark:text-white/50 hover:text-gray-700 dark:hover:text-gray-200'
                                                    }`}
                                                >
                                                    {f.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Duration selector */}
                                <p className="text-xs font-semibold text-gray-500 dark:text-white/50 mb-2 uppercase tracking-wide">
                                    {language === 'fr' ? 'Durée' : 'Duration'}
                                </p>
                                <div className="grid grid-cols-3 gap-3">
                                    {durations.map(d => {
                                        const price = getPrice(d, selectedFormula);
                                        if (price == null) return null;
                                        const isSelected = selectedDuration === d;
                                        return (
                                            <button
                                                key={d}
                                                onClick={() => setSelectedDuration(d)}
                                                className={`py-3 px-2 rounded-xl text-center transition-all border-2 ${
                                                    isSelected
                                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                                                        : 'border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20'
                                                }`}
                                            >
                                                <div className={`text-xs font-semibold ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-white/50'}`}>
                                                    {getDurationLabel(d)}
                                                </div>
                                                <div className={`text-lg font-bold mt-1 ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'}`}>
                                                    ${price.toFixed(0)}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer — Buy button */}
                {!level.isFree && hasPricing && currentPrice != null && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-white/10">
                        <button
                            onClick={handleBuy}
                            className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-green-500/25 hover:shadow-green-500/40"
                        >
                            {language === 'fr' ? `Acheter — $${currentPrice.toFixed(2)}` : `Buy — $${currentPrice.toFixed(2)}`}
                        </button>
                        {!user && (
                            <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-2">
                                {language === 'fr' ? 'Connexion requise pour acheter' : 'Login required to purchase'}
                            </p>
                        )}
                    </div>
                )}
            </motion.div>

            {showProfileModal && (
                <ProfileCompletionModal
                    onComplete={() => { setShowProfileModal(false); setShowCheckout(true); }}
                    onClose={() => setShowProfileModal(false)}
                />
            )}

            {showCheckout && (
                <CheckoutModal
                    paymentData={{
                        levelId: level.idNiveau,
                        levelVar: level.var,
                        duration: selectedDuration,
                        formula: selectedFormula,
                    }}
                    onClose={() => setShowCheckout(false)}
                />
            )}
        </motion.div>
    );
};

export default LevelDetailModal;
